// ─────────────────────────────────────────────────────────────────────────────
// ChatSurface — CI-6 correction (post-Ghost): iOS chat shape, agent-nav-free.
//
// FLAG 1 INVARIANT (Ghost): this component declares **ZERO useState** and
// **ZERO useEffect**. Every piece of state and every handler lives in App.tsx
// and flows in via props. ChatSurface receives, ChatSurface renders. Nothing
// moves out. Function: untouched, full stop.
//
// LOCKED RULES enforced here (re-affirmed by user during CI-6):
//   • Agents are reached ONLY via domain dashboard agent cards. No in-chat
//     agent picker, no slide-in agent overlay, no ☰. AgentSidebar is NOT
//     imported by this file. The component still exists in the codebase for
//     other call sites; it just does not appear inside the chat surface.
//   • The chat header contains only the iOS-shape thread switcher
//     (ChatThreadsBar), nothing else. To change agents the user goes back to
//     the domain dashboard via AppShell's back chevron.
//
// LAYOUT (matches iOS multi-chat shape):
//   ┌─────────┬───────────────────────────────────────────────┐
//   │ Threads │ MessageList (transparent over atmosphere)    │
//   │ bar     ├───────────────────────────────────────────────┤
//   │ (iOS)   │ InputBox (translucent / atmosphere-tinted)   │
//   └─────────┴───────────────────────────────────────────────┘
//   Dashboard banner row (CODE/SECURITY/Arthur "Open Dashboard ↗") still
//   renders above the row when `showDashboardBanners` is true — preserves
//   the Flag 5 safety net for legacy call sites. AgentViewScreen passes
//   false, so banners do not appear in the new tree.
//
// Memory Manager + Security Dashboard overlays stay as `fixed inset-0`
// (preserved from CI-3 — covers full viewport).
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import ChatThreadsBar from './ChatThreadsBar';
import { type Thread } from '../ChatThreads';
import ChatWindow from '../ChatWindow';
import InputBox from '../InputBox';
// CI-4: cycle-guard. Message + Status are type-only across the App.tsx ↔
// ChatSurface boundary — keep them out of the runtime import graph.
import type { Message, Status } from '../../App';
import { getDomain } from '../../lib/agents';

export interface ChatSurfaceProps {
  // ── identity (read-only, computed in App.tsx) ──
  activeDomain: string;
  activeAgent: string | null;
  authToken: string | null;
  status: Status | null;

  // ── chat content ──
  messages: Message[];
  isLoading: boolean;

  // ── threads (multi-chat) ──
  threads: Thread[];
  activeThreadId: string | null;

  // ── threads bar collapse (reuses the App.tsx `sidebarOpen` prop;
  //    in the new design true = expanded threads bar, false = collapsed) ──
  sidebarOpen: boolean;
  setSidebarOpen: (v: boolean) => void;

  // ── override menu state (forwarded to InputBox) ──
  overrideMode: boolean;
  overrideApi: string | null;
  showOverrideMenu: boolean;
  setShowOverrideMenu: React.Dispatch<React.SetStateAction<boolean>>;

  // ── The OS-wide escalation toggle (forwarded to InputBox) ──
  // routingMode is DERIVED from it; frontier_only is retired and no longer a state.
  routingMode: 'local_only' | 'local_frontier';
  escalation: boolean;
  escalationLoaded: boolean;
  localLaneUp: boolean;
  toggleEscalation: (next: boolean) => void | Promise<void>;

  // ── D4 — local-down fallback target (forwarded to InputBox) ──
  fallbackTarget: 'claude' | 'gpt' | 'none';
  setFallbackTarget: React.Dispatch<
    React.SetStateAction<'claude' | 'gpt' | 'none'>
  >;

  // ── model menu state (forwarded to InputBox) ──
  activeModel: string;
  setActiveModel: (m: string) => void;
  showModelMenu: boolean;
  setShowModelMenu: React.Dispatch<React.SetStateAction<boolean>>;
  ALL_MODELS: string[];

  // ── memory state ──
  memoryOverride: boolean;
  lastMemoryStatus: 'saved' | 'blocked' | 'override' | null; // (kept in props for future use)

  // ── security dashboard overlay state ──
  showSecurityDash: boolean;
  setShowSecurityDash: (v: boolean) => void;
  securityFlags: any[];
  securitySummary: { high?: number; medium?: number; low?: number };
  securityLevel: string;
  setSecurityLevel: (l: string) => void;

  // ── memory manager overlay state ──
  showMemoryManager: boolean;
  setShowMemoryManager: (v: boolean) => void;
  memoryEntries: any[];
  memoryDomain: string;
  memoryFilter: string;
  setMemoryFilter: (f: string) => void;

  // ── chat-header banner visibility (Flag 6 wrapper retirement) ──
  showDashboardBanners: boolean;
  arthurContextLabel?: string;

  // ── 6.8 — hide the file-attach affordance (homepage Rexy chat; HOMEPAGE
  //    uploads are backend-walled). Optional, defaults off — nav agents unaffected. ──
  uploadsDisabled?: boolean;

  // ── handlers (every existing handler from App.tsx; ChatSurface defines NONE) ──
  onSendMessage: (text: string) => Promise<void>;
  onSendWithFile: (text: string, file: File) => Promise<void>;
  onSendWithFiles: (text: string, files: File[]) => Promise<void>;
  onFileUpload: (file: File) => Promise<void>;
  onNewThread: () => Promise<void>;
  onSelectThread: (id: string) => void;
  onEndThread: (id: string) => Promise<void>;
  onRenameThread: (id: string, name: string) => Promise<void>;
  onDeleteThread: (id: string) => Promise<void>;
  // Resolves to WHETHER THE AGENT ACTIVATED (owner ruling 2026-08-24). The
  // handler used to swallow its own failure and resolve void, so a caller had
  // no way to tell a real activation from one that never happened — which is
  // how a chat opened for an agent that was never set. Consumers that only
  // switch agents may ignore it; the one that NAVIGATES must not.
  onAgentChange: (agent: string | null) => Promise<boolean>;
  onOverrideToggle: (active: boolean, api?: string) => Promise<void>;
  onMemoryOverrideToggle: () => Promise<void>;
  onOpenSecurity: () => void;
  onQuit: () => Promise<void>;
  onFetchSecurity: (level?: string) => Promise<void>;
  onFetchMemory: (domain?: string) => Promise<void>;

  // ── legacy banner click-throughs (Flag 5 safety-net) ──
  onOpenCodeDashboard: () => void;
  onOpenSecurityDashboard: () => void;
  onOpenArthurDashboard: () => void;
}

export default function ChatSurface(props: ChatSurfaceProps) {
  const {
    activeDomain,
    activeAgent,
    authToken,
    status,
    messages,
    isLoading,
    threads,
    activeThreadId,
    sidebarOpen,
    setSidebarOpen,
    overrideMode,
    overrideApi,
    showOverrideMenu,
    setShowOverrideMenu,
    routingMode,
    escalation,
    escalationLoaded,
    localLaneUp,
    toggleEscalation,
    fallbackTarget,
    setFallbackTarget,
    activeModel,
    setActiveModel,
    showModelMenu,
    setShowModelMenu,
    ALL_MODELS,
    memoryOverride,
    showSecurityDash,
    setShowSecurityDash,
    securityFlags,
    securitySummary,
    securityLevel,
    setSecurityLevel,
    showMemoryManager,
    setShowMemoryManager,
    memoryEntries,
    memoryDomain,
    memoryFilter,
    setMemoryFilter,
    showDashboardBanners,
    uploadsDisabled,
    onSendMessage,
    onSendWithFile,
    onSendWithFiles,
    onFileUpload,
    onNewThread,
    onSelectThread,
    onEndThread,
    onRenameThread,
    onDeleteThread,
    onOverrideToggle,
    onMemoryOverrideToggle,
    onOpenSecurity,
    onQuit,
    onFetchSecurity,
    onFetchMemory,
    onOpenCodeDashboard,
    onOpenSecurityDashboard,
    onOpenArthurDashboard,
  } = props;

  // Domain accent for bubbles + active-thread strip — lockstep with the agent
  // (mirrors iOS ChatScreen which computes acc from AGENT_ACCENTS once and
  // passes to MessageList / ChatSidebar).
  const dom = getDomain(activeDomain);
  const accentColor = dom?.accent ?? '#d4d4d4';
  const accentMuted = dom?.accentMuted ?? '#404040';

  return (
    <div className="flex flex-col h-full text-rexy-text overflow-hidden relative">
      {activeDomain === 'TRADING' && activeAgent?.toLowerCase() === 'arthur' && props.arthurContextLabel && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-rexy-border bg-rexy-card text-xs text-amber-400">
          <span>{props.arthurContextLabel}</span>
          <button onClick={onOpenArthurDashboard} className="px-2 py-1 rounded border border-rexy-border">Session controls</button>
        </div>
      )}

      {/* Dashboard banners — Flag 5 safety-net; gated by showDashboardBanners.
          AgentViewScreen passes false in the Chat tab; legacy paths pass true. */}
      {showDashboardBanners && activeDomain === 'CODE' && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-rexy-border bg-rexy-card/50">
          <span className="text-[10px] text-purple-400/70 font-bold uppercase tracking-widest">Code Workspace</span>
          <button
            onClick={onOpenCodeDashboard}
            className="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded border border-purple-500/30 text-purple-400/80 hover:bg-purple-900/20 hover:text-purple-400 transition-colors"
          >
            Open Workspace ↗
          </button>
        </div>
      )}
      {showDashboardBanners && activeDomain === 'SECURITY' && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-rexy-border bg-rexy-card/50">
          <span className="text-[10px] text-red-400/70 font-bold uppercase tracking-widest">Security Intelligence</span>
          <button
            onClick={onOpenSecurityDashboard}
            className="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded border border-red-500/30 text-red-400/80 hover:bg-red-900/20 hover:text-red-400 transition-colors"
          >
            Open Dashboard ↗
          </button>
        </div>
      )}
      {showDashboardBanners && activeAgent === 'arthur' && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-rexy-border bg-rexy-card/50">
          <span className="text-[10px] text-emerald-400/70 font-bold uppercase tracking-widest">Arthur — Trading Dashboard</span>
          <button
            onClick={onOpenArthurDashboard}
            className="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded border border-emerald-500/30 text-emerald-400/80 hover:bg-emerald-900/20 hover:text-emerald-400 transition-colors"
          >
            Open Dashboard ↗
          </button>
        </div>
      )}

      {/* Main row — threads bar (iOS shape) + (messages stacked over input) */}
      <div className="flex-1 flex overflow-hidden relative">

        {/* iOS-shape collapsible thread bar — only meaningful with an active
            agent (matches existing semantic: per-agent threads). */}
        {activeAgent && (
          <ChatThreadsBar
            threads={threads}
            activeThreadId={activeThreadId}
            collapsed={!sidebarOpen}
            onToggleCollapse={() => setSidebarOpen(!sidebarOpen)}
            onSelectThread={onSelectThread}
            onNewThread={onNewThread}
            onEndThread={onEndThread}
            onRenameThread={onRenameThread}
            onDeleteThread={onDeleteThread}
            accentColor={accentColor}
          />
        )}

        {/* Right column: messages + input stacked. */}
        <div className="flex-1 flex flex-col overflow-hidden relative">
          {/* min-h-0 + flex-col so ChatWindow's `flex-1 overflow-y-auto` resolves a
              bounded height and the thread scrolls back through history. Without the
              flex container the child's flex-1 is inert → content overflows and is
              clipped (newest pinned by auto-scroll, but no manual scroll-back). */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden relative">
            <ChatWindow
              messages={messages}
              activeAgent={activeAgent}
              authToken={authToken}
              accentColor={accentColor}
              accentMuted={accentMuted}
            />

            {/* Loading indicator (preserved CI-3 styling) */}
            {isLoading && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2
                bg-rexy-card/90 px-4 py-2 rounded-full border border-rexy-border
                backdrop-blur-sm z-10 flex items-center gap-2 shadow-lg">
                <div className="w-3 h-3 border-2 border-rexy-accent
                  border-t-transparent rounded-full animate-spin" />
                <span className="text-xs font-bold tracking-widest
                  text-rexy-accent animate-pulse">
                  REASONING...
                </span>
              </div>
            )}
          </div>

          {/* iOS-shape InputBar (CI-6.2) — translucent atmosphere-tinted glass,
              toolbar + overflow holds the lifted status-bar controls. */}
          <InputBox
            onSendMessage={onSendMessage}
            onOverrideToggle={onOverrideToggle}
            overrideMode={overrideMode}
            onFileUpload={onFileUpload}
            onSendWithFile={onSendWithFile}
            onSendWithFiles={onSendWithFiles}
            activeAgent={activeAgent}
            activeDomain={activeDomain}
            turnCount={messages.length}
            authToken={authToken}
            uploadsDisabled={uploadsDisabled}
            activeModel={activeModel}
            setActiveModel={setActiveModel}
            showModelMenu={showModelMenu}
            setShowModelMenu={setShowModelMenu}
            ALL_MODELS={ALL_MODELS}
            overrideApi={overrideApi}
            showOverrideMenu={showOverrideMenu}
            setShowOverrideMenu={setShowOverrideMenu}
            routingMode={routingMode}
            escalation={escalation}
            escalationLoaded={escalationLoaded}
            localLaneUp={localLaneUp}
            toggleEscalation={toggleEscalation}
            fallbackTarget={fallbackTarget}
            setFallbackTarget={setFallbackTarget}
            memoryOverride={memoryOverride}
            onMemoryOverrideToggle={onMemoryOverrideToggle}
            status={status}
            setShowMemoryManager={setShowMemoryManager}
            onFetchMemory={onFetchMemory}
            memoryDomain={memoryDomain}
            onOpenSecurity={onOpenSecurity}
            onQuit={onQuit}
          />
        </div>
      </div>

      {/* Memory Manager Overlay (preserved from CI-3, fixed inset-0 covers viewport) */}
      {showMemoryManager && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center
          justify-center" onClick={() => setShowMemoryManager(false)}>
          <div className="bg-rexy-card border border-rexy-border rounded-xl
            shadow-2xl w-[680px] max-h-[80vh] flex flex-col"
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4
              border-b border-rexy-border">
              <div>
                <h2 className="text-sm font-bold text-rexy-text uppercase
                  tracking-widest">Memory Manager</h2>
                <span className="text-[10px] text-rexy-muted">
                  {memoryEntries.length} entries · {memoryDomain}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {['PERSONAL','TRADING','BUSINESS','GROWTH','RESEARCH','MEDIA'].map(d => (
                  <button key={d}
                    onClick={() => onFetchMemory(d)}
                    className={`text-[9px] font-bold uppercase tracking-widest
                      px-2 py-1 rounded transition-colors ${
                      memoryDomain === d
                        ? 'bg-rexy-border text-rexy-text'
                        : 'text-rexy-muted hover:text-rexy-text'
                    }`}
                  >{d}</button>
                ))}
                <select
                  value={memoryFilter}
                  onChange={e => setMemoryFilter(e.target.value)}
                  className="text-[9px] bg-rexy-dark border border-rexy-border
                    rounded px-1.5 py-1 text-rexy-muted uppercase tracking-widest
                    focus:outline-none ml-1"
                >
                  <option value="all">All</option>
                  <option value="override">Override</option>
                  <option value="import">Imported</option>
                </select>
                <button onClick={() => setShowMemoryManager(false)}
                  className="ml-2 p-1.5 rounded hover:bg-rexy-border
                    text-rexy-muted hover:text-rexy-text transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Entry list */}
            <div className="flex-1 overflow-y-auto px-5 py-3 flex flex-col gap-2">
              {memoryEntries.length === 0 ? (
                <div className="text-center py-12 text-rexy-muted text-sm">
                  No entries in {memoryDomain} memory
                </div>
              ) : memoryEntries
                .filter(e => {
                  if (memoryFilter === 'override') return e.import_batch;
                  if (memoryFilter === 'import') return e.source === 'file_upload';
                  return true;
                })
                .slice(0, 50)
                .map((entry: any, i: number) => (
                  <div key={i} className="px-3 py-2.5 rounded-lg border
                    border-rexy-border/50 bg-rexy-dark/50 text-[11px]">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="text-rexy-text font-medium truncate flex-1">
                        {entry.goal || entry.filename || 'No goal'}
                      </span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {entry.import_batch && (
                          <span className="text-[8px] font-bold uppercase
                            text-orange-400/80 bg-orange-400/10 px-1.5 py-0.5
                            rounded">override</span>
                        )}
                        {entry.agent_space && (
                          <span className="text-[8px] font-bold uppercase
                            text-rexy-muted bg-rexy-border px-1.5 py-0.5
                            rounded">{entry.agent_space}</span>
                        )}
                        {entry.topics && entry.topics.length > 0 && (
                          <span className="text-[8px] text-rexy-muted/60">
                            #{entry.topics.slice(0,3).join(' #')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-rexy-muted/70 leading-relaxed line-clamp-2">
                      {entry.observations?.[0]?.content?.slice(0, 120) ||
                       entry.insights?.[0]?.summary?.slice(0, 120) || '—'}
                    </div>
                    <div className="text-[9px] text-rexy-muted/40 mt-1">
                      {entry.project_id} · {entry.timestamp?.slice(0,10) || ''}
                    </div>
                  </div>
                ))}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-rexy-border flex
              justify-between items-center">
              <span className="text-[9px] text-rexy-muted">
                Showing up to 50 entries
              </span>
              <button onClick={() => onFetchMemory(memoryDomain)}
                className="text-[10px] font-bold uppercase tracking-widest
                  text-rexy-muted hover:text-rexy-text transition-colors">
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Security Dashboard Overlay (preserved from CI-3, fixed inset-0) */}
      {showSecurityDash && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center
          justify-center" onClick={() => setShowSecurityDash(false)}>
          <div className="bg-rexy-card border border-rexy-border rounded-xl
            shadow-2xl w-[640px] max-h-[80vh] flex flex-col"
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4
              border-b border-rexy-border">
              <div>
                <h2 className="text-sm font-bold text-rexy-text uppercase
                  tracking-widest">Security Dashboard</h2>
                <div className="flex gap-3 mt-1">
                  <span className="text-[10px] text-red-400 font-bold">
                    🔴 {securitySummary.high || 0} HIGH
                  </span>
                  <span className="text-[10px] text-amber-400 font-bold">
                    🟡 {securitySummary.medium || 0} MEDIUM
                  </span>
                  <span className="text-[10px] text-rexy-muted font-bold">
                    🟢 {securitySummary.low || 0} LOW
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(['high','medium','all'] as const).map(l => (
                  <button key={l}
                    onClick={() => { setSecurityLevel(l); onFetchSecurity(l); }}
                    className={`text-[9px] font-bold uppercase tracking-widest
                      px-2 py-1 rounded transition-colors ${
                      securityLevel === l
                        ? 'bg-rexy-border text-rexy-text'
                        : 'text-rexy-muted hover:text-rexy-text'
                    }`}
                  >{l}</button>
                ))}
                <button onClick={() => setShowSecurityDash(false)}
                  className="ml-2 p-1.5 rounded hover:bg-rexy-border
                    text-rexy-muted hover:text-rexy-text transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Flags list */}
            <div className="flex-1 overflow-y-auto px-5 py-3 flex flex-col gap-2">
              {securityFlags.length === 0 ? (
                <div className="text-center py-12 text-rexy-muted text-sm">
                  ✓ No flags at this level
                </div>
              ) : securityFlags.map((flag, i) => (
                <div key={i} className={`px-3 py-2.5 rounded-lg border
                  text-[11px] ${
                  flag.risk === 'high'
                    ? 'border-red-800/40 bg-red-900/10'
                    : 'border-amber-800/30 bg-amber-900/10'
                }`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`font-bold uppercase tracking-widest
                      text-[10px] ${
                      flag.risk === 'high' ? 'text-red-400' : 'text-amber-400'
                    }`}>
                      {flag.risk === 'high' ? '🔴' : '🟡'} {flag.event}
                    </span>
                    <span className="text-rexy-muted text-[9px]">
                      {new Date(flag.timestamp).toLocaleTimeString('en-AU')}
                    </span>
                  </div>
                  <div className="text-rexy-muted">
                    {Object.entries(flag.details || {})
                      .filter(([k]) => !['path'].includes(k))
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(' · ')}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-rexy-border flex
              justify-between items-center">
              <span className="text-[9px] text-rexy-muted">
                Showing last 100 flagged events
              </span>
              <button onClick={() => onFetchSecurity(securityLevel)}
                className="text-[10px] font-bold uppercase tracking-widest
                  text-rexy-muted hover:text-rexy-text transition-colors">
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
